import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {produce} from 'immer'
import { Grid, Menu, MenuItem } from '@mui/material'
import { ROUTES } from '../../../../constants'
import { TStringOrNumber } from 'models/shared.models'
import { TCFDIVersion } from 'models/invoices.models'
import useGetSelectedEmisor from 'hooks/Shared/useGetSelectedEmisor'
import useDialog from 'hooks/Shared/useDialog'
import useLoader from 'hooks/Shared/useLoader'
import useSyncCFDIs from 'hooks/Invoices/CFDIs/useSyncCFDIs'
import { setSelectedEmisor } from 'actions/invoices'
import CFDIsContainer from 'containers/Invoices/CFDIs/CFDIsContainer/CFDIsContainer'
import CFDIsXMLUploadContainer from 'containers/Invoices/CFDIs/CFDIsXMLUploadContainer/CFDIsXMLUploadContainer'
import CFDIsZIPUploadContainer from 'containers/Invoices/CFDIs/CFDIsZIPUploadContainer/CFDIsZIPUploadContainer'
import SydDrawer from 'components/Shared/SydDrawer/SydDrawer'
import SyncCFDIsDialog from 'components/Shared/SyncCFDIsDialog/SyncCFDIsDialog'
import InvoicesActions from 'components/Invoices/CFDIs/InvoicesActions/InvoicesActions'
import NewCFDIDialog from 'components/Invoices/NewCFDI/NewCFDIDialog/NewCFDIDialog'
import './styles.scss'

interface ICFDIsScreenState {
  anchorEl: HTMLElement | null
  isUploadDrawerOpen: boolean
  isUploadZipDrawerOpen: boolean
  selectedXMLFiles: File[]
  selectedZipFile: File | null
}

const CFDIsScreen = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const loader = useLoader()
  const dialog = useDialog()

  const [openNewDialog, setOpenNewDialog] = useState(false)
  const [cfdisScreenState, setCFDIsScreenState] = useState<ICFDIsScreenState>({
    anchorEl: null,
    isUploadDrawerOpen: false,
    isUploadZipDrawerOpen: false,
    selectedXMLFiles: [],
    selectedZipFile: null,
  })

  const {
    anchorEl,
    isUploadDrawerOpen,
    isUploadZipDrawerOpen,
    selectedXMLFiles,
    selectedZipFile,
  } = cfdisScreenState

  const updateCFDIsScreenState = useCallback(
    (update: (draft: ICFDIsScreenState) => void) => {
      setCFDIsScreenState(currentState => produce(currentState, update))
    },
    []
  )

  const uploadInputRef = useRef<HTMLInputElement | null>(null)
  const uploadInputZipRef = useRef<HTMLInputElement | null>(null)

  const { emisor, emisoresQuery } = useGetSelectedEmisor(() =>
    navigate(ROUTES.HOME, { replace: true })
  )

  const { isCFDIsSyncOpen, toggleSyncDialog, syncMutation } = useSyncCFDIs({
    emisoresQuery,
    emisor,
  })

  useEffect(() => {
    emisoresQuery.isLoading
      ? loader.primary('Cargando emisores')
      : loader.close()
  }, [loader, emisoresQuery.isLoading])

  const onRouteChange = (
    action: 'addPaymentComplement' | 'addCreditNote',
    id: number,
    version: TCFDIVersion = '3.3'
  ) => {
    action === 'addPaymentComplement' &&
      navigate(
        ROUTES.ADD_PAYMENT_COMPLEMENT.replace(':invoice', `${id}`).replace(
          ':version',
          `${version}`
        )
      )
    action === 'addCreditNote' &&
      navigate(ROUTES.ADD_CREDIT_NOTE.replace(':invoice', `${id}`))
  }

  const handleNewCFDIButton = useCallback(
    (value: TStringOrNumber) => {
      if (value === 0) {
        setOpenNewDialog(true)
      } else if (value === 1 && uploadInputRef !== null) {
        uploadInputRef?.current && uploadInputRef.current.click()
      } else if (value === 2 && uploadInputZipRef !== null) {
        uploadInputZipRef?.current && uploadInputZipRef.current.click()
      }
    },
    [uploadInputRef, uploadInputZipRef]
  )

  const handleXMLFilesSelection = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files as FileList
      if (files !== undefined && files.length > 0) {
        updateCFDIsScreenState(draft => {
          draft.selectedXMLFiles = Array.from(files)
          draft.isUploadDrawerOpen = true
        })
      }
    },
    [updateCFDIsScreenState]
  )

  const handleCancelZipSizeError = useCallback(() => {
    if (uploadInputZipRef && uploadInputZipRef.current) {
      uploadInputZipRef.current.value = ''
    }
  }, [uploadInputZipRef])

  const fireZipSizeErrorModal = useCallback(
    (fileName: string, primaryAction: () => void, cancelAction: () => void) =>
      dialog.danger({
        title: 'El archivo seleccionado supera el tamaño permitido',
        subtitle: `El archivo seleccionado supera el tamaño máximo permitido de 10 MB. Te sugerimos elegir un archivo más pequeño.`,
        primaryButtonText: 'Elegir otro archivo',
        secondaryButtonText: 'Cancelar',
        modalContent: (
          <div>
            <span>{fileName}</span>
          </div>
        ),
        onClose: cancelAction,
        onPrimaryClick: primaryAction,
        onSecondaryClick: cancelAction,
      }),
    [dialog]
  )

  const handleZIPSelection = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files as FileList
      if (files !== undefined && files.length > 0) {
        const selectedZipFile = Array.from(files)[0]
        if (selectedZipFile.size < 10485760 + 1) {
          updateCFDIsScreenState(draft => {
            draft.selectedZipFile = Array.from(files)[0]
            draft.isUploadZipDrawerOpen = true
          })

          return
        }

        fireZipSizeErrorModal(
          selectedZipFile.name,
          () => handleNewCFDIButton(2),
          handleCancelZipSizeError
        )
      }
    },
    [
      fireZipSizeErrorModal,
      handleCancelZipSizeError,
      handleNewCFDIButton,
      updateCFDIsScreenState,
    ]
  )

  const closeEmisorMenu = () =>
    updateCFDIsScreenState(draft => {
      draft.anchorEl = null
    })

  const closeUploadDrawer = useCallback(() => {
    if (uploadInputRef && uploadInputRef.current) {
      uploadInputRef.current.value = ''
    }
    updateCFDIsScreenState(draft => {
      draft.isUploadDrawerOpen = false
      draft.selectedXMLFiles = []
    })
  }, [updateCFDIsScreenState, uploadInputRef])

  const closeUploadZipDrawer = useCallback(() => {
    if (uploadInputZipRef && uploadInputZipRef.current) {
      uploadInputZipRef.current.value = ''
    }
    updateCFDIsScreenState(draft => {
      draft.isUploadZipDrawerOpen = false
      draft.selectedZipFile = null
    })
  }, [updateCFDIsScreenState, uploadInputZipRef])

  useEffect(() => {
    emisoresQuery.isLoading
      ? loader.primary('Cargando emisores')
      : loader.close()
  }, [loader, emisoresQuery.isLoading])

  return (
    <>
      <Grid container spacing={0} className="screen-cfdis">
        <Grid item xs={12}>
          <div className="title-container">
            <h1 className="title">Mis CFDIs</h1>
            <div className="title-actions">
              <InvoicesActions
                uploadInputRef={uploadInputRef}
                uploadInputZipRef={uploadInputZipRef}
                handleNewCFDIButton={handleNewCFDIButton}
                handleXMLFilesSelection={handleXMLFilesSelection}
                handleZIPSelection={handleZIPSelection}
                toggleSyncDialog={toggleSyncDialog}
              />
            </div>
          </div>
        </Grid>

        <Grid item xs={12}>
          {!(emisoresQuery.isLoading || emisoresQuery.isError) && (
            <CFDIsContainer
              emisor={emisor || null}
              onRedirect={onRouteChange}
            />
          )}
        </Grid>
      </Grid>

      <SyncCFDIsDialog
        open={isCFDIsSyncOpen}
        loading={syncMutation.isLoading}
        toggleSyncDialog={toggleSyncDialog}
        syncCFDIS={syncMutation}
      />

      <NewCFDIDialog
        open={openNewDialog}
        onClose={() => setOpenNewDialog(false)}
      />

      <Menu
        id="selectEmisorMenu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={closeEmisorMenu}
        className="screen-emisor-list">
        {emisoresQuery.data?.map((e, i) => (
          <MenuItem
            key={i}
            className="screen-emisor-element"
            disabled={emisor?.idRfc === e.idRfc}
            selected={emisor?.idRfc === e.idRfc}
            onClick={() => {
              dispatch(setSelectedEmisor(e))
              closeEmisorMenu()
            }}>
            <span className="name">{e.name}</span>
            <span className="rfc">{e.rfc}</span>
          </MenuItem>
        ))}
      </Menu>

      <SydDrawer
        isOpen={isUploadDrawerOpen}
        handleClose={closeUploadDrawer}
        title="Archivos cargados">
        <CFDIsXMLUploadContainer
          files={selectedXMLFiles}
          handleCloseDrawer={closeUploadDrawer}
        />
      </SydDrawer>

      <SydDrawer
        isOpen={isUploadZipDrawerOpen}
        handleClose={closeUploadZipDrawer}
        title="Archivos cargados">
        <CFDIsZIPUploadContainer
          file={selectedZipFile}
          fireZipSizeErrorModal={fireZipSizeErrorModal}
          handleCloseDrawer={closeUploadZipDrawer}
        />
      </SydDrawer>
    </>
  )
}

export default CFDIsScreen
